import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  ExecutionJobSummary,
  ExecutionJobsParams,
  getExecutionJobs,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface ExecutionSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const JOB_TYPE_LABELS: Record<string, string> = {
  ENTRY: 'Entry',
  EXIT: 'Exit',
  ADJUST_STOP: 'Adjust SL',
  ADJUST_TP: 'Adjust TP',
  DEFERRED_EXIT: 'Deferred Exit',
};

const STATUS_COLORS: Record<string, string> = {
  succeeded: 'badgePass',
  queued: 'badgePending',
  running: 'badgePending',
  failed: 'badgeFail',
  dead: 'badgeDead',
  skipped: 'badgeMuted',
};

const ExecutionSection: React.FC<ExecutionSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [jobType, setJobType] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');

  const params = useMemo<ExecutionJobsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    job_type: jobType || undefined,
    status: statusFilter || undefined,
  }), [dateFrom, dateTo, symbol, jobType, statusFilter]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } = useFlowData<ExecutionJobSummary, ExecutionJobsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'execution'],
    fetchFn: (p) => getExecutionJobs(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  return (
    <DataSection
      title="Execution Jobs"
      tabNumber={11}
      description="Execution queue tracking: entry orders, exit orders, adjustments. Shows job status, attempts, and errors."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <select
          value={jobType}
          onChange={(e) => { setJobType(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="">All Types</option>
          {Object.entries(JOB_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="">All Status</option>
          <option value="queued">Queued</option>
          <option value="running">Running</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Failed</option>
          <option value="dead">Dead</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Job ID</th>
            <th>Symbol</th>
            <th>Type</th>
            <th>Status</th>
            <th>Attempts</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {data.map((job) => {
            const statusClass = STATUS_COLORS[job.status || ''] || '';
            return (
              <tr key={job.id}>
                <td className={styles.monoSmall}>{job.id.slice(0, 16)}...</td>
                <td className={styles.mono}>{job.symbol || '-'}</td>
                <td><span className={styles.badgeMuted}>{JOB_TYPE_LABELS[job.job_type || ''] || job.job_type}</span></td>
                <td>
                  <span className={`${styles.badgeSmall} ${styles[statusClass]}`}>
                    {job.status || '-'}
                  </span>
                </td>
                <td>{job.attempt_count}</td>
                <td className={styles.errorCell}>{job.error_message || '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </DataSection>
  );
};

export default ExecutionSection;
